import React from "react";
import { DateRenderer } from "../shared/renderers/table";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { defineMessages, FormattedMessage } from "react-intl";
import {
  Taxation,
  TaxationRestrictions,
  TaxationThreshold,
  TaxationTranslation,
} from "../../gen/witbooking/backoffice/taxation/v1/taxation_api_pb";
import { RendererProps } from "../shared/renderers";
import { cloneDeep } from "lodash";
import { asUTC } from "../../lib/dateUtils";
import styled from "styled-components";
import TaxationTypology = Taxation.TaxationTypology;

export const tableHeaderMessages = defineMessages({
  active: { id: "taxation.activeList", defaultMessage: "active" },
  ticker: { id: "taxation.tickerList", defaultMessage: "ticker" },
  title: { id: "taxation.titleList", defaultMessage: "title" },
  typology: { id: "taxation.typologyList", defaultMessage: "typology" },
  amount: { id: "taxation.amountList", defaultMessage: "amount" },
  threshold: { id: "taxation.thresholdList", defaultMessage: "threshold" },
  maxNights: { id: "taxation.maxNightsList", defaultMessage: "maxNights" },
  maxAmount: {
    id: "taxation.maxAmountList",
    defaultMessage: "maxAmount",
  },
});
export const messages = {
  ...defaultCrudMessages,
  tableHeaders: tableHeaderMessages,
  entityName: {
    id: "taxation.taxes",
    defaultMessage: "Taxes",
  },
  active: { id: "taxation.active", defaultMessage: "active" },
  ticker: { id: "taxation.ticker", defaultMessage: "ticker" },
  title: { id: "taxation.title", defaultMessage: "title" },
  description: { id: "taxation.description", defaultMessage: "description" },
  translations: { id: "taxation.translations", defaultMessage: "translations" },
  typology: { id: "taxation.typology", defaultMessage: "typology" },
  validity: { id: "taxation.validity", defaultMessage: "validity" },
  maxNights: { id: "taxation.maxNights", defaultMessage: "maxNights" },
  maxNightsHelpText: { id: "taxation.maxNightsHelpText", defaultMessage: "maxNightsHelpText" },
  maxAmount: { id: "taxation.maxAmount", defaultMessage: "maxAmount" },
  maxAmountHelpText: { id: "taxation.maxAmountHelpText", defaultMessage: "maxAmountHelpText" },
  restrictions: { id: "taxation.restrictions", defaultMessage: "restrictions" },
  perPerson: { id: "taxation.perPerson", defaultMessage: "perPerson" },
  perPersonPerNight: { id: "taxation.perPersonPerNight", defaultMessage: "perPersonPerNight" },
  perStay: { id: "taxation.perStay", defaultMessage: "perStay" },
  perStayPerNight: { id: "taxation.perStayPerNight", defaultMessage: "perStayPerNight" },
  percentage: { id: "taxation.percentage", defaultMessage: "percentage" },
  balearic: { id: "taxation.balearic", defaultMessage: "balearic" },
  noLimit: { id: "taxation.noLimit", defaultMessage: "noLimit" },
  deleteSuccess: { id: "app.entityList.deleteSuccess", defaultMessage: "deleteSuccess" },
  deleteError: { id: "app.entityList.deleteError", defaultMessage: "deleteError" },
};
export const messagesFees = {
  ...messages,
  entityName: {
    id: "taxation.fees",
    defaultMessage: "Fees",
  },
};

export const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: left;

  & .witadmin-bs3 .form-group {
    margin-bottom: 0;
  }

  & .witadmin-bs3 label {
    margin-bottom: 0;
  }

  & em {
    cursor: default !important;
  }
`;

export enum TYPE {
  FEE = "FEE",
  TAX = "TAX",
}

export enum Typology {
  PER_PERSON = "PER_PERSON",
  PER_PERSON_PER_NIGHT = "PER_PERSON_PER_NIGHT",
  PER_STAY = "PER_STAY",
  PER_STAY_PER_NIGHT = "PER_STAY_PER_NIGHT",
  PERCENTAGE = "PERCENTAGE",
  BALEARIC = "BALEARIC",
}

export interface CustomTaxation extends Taxation.AsObject {
  type: TYPE;
  typologyModule?: {
    typology: Taxation.TaxationTypology;
    amount: number;
    minAge: number;
    typeOfTaxation: TYPE;
    maxNights?: number;
    maxAmount?: number;
  };
  taxPreferenceModule?: {
    preference: Taxation.TaxationPreference;
  };
  validitySection?: string;
  restrictionsSection?: string;
  maxNights?: number;
  maxAmount?: number;
  thresholdModule?: { from: string; to: string };
}

export function taxationFromObject(obj: CustomTaxation, type: TYPE): Taxation {
  const result = new Taxation();

  const translationsList = obj.translationsList.map(it => {
    const translations = new TaxationTranslation();
    translations.setLocale(it.locale);
    translations.setTitle(it.title);
    translations.setDescription(it.description);
    return translations;
  });

  const threshold = new TaxationThreshold();
  if (obj.thresholdModule?.from) {
    // @ts-ignore
    const startAt = new proto.google.protobuf.Timestamp();
    const startDate = new Date(obj.thresholdModule?.from);
    startAt.fromDate(startDate);
    threshold.setStartAt(startAt);
  }

  if (obj.thresholdModule?.to) {
    // @ts-ignore
    const endAt = new proto.google.protobuf.Timestamp();
    const endDate = new Date(obj.thresholdModule?.to);
    endAt.fromDate(endDate);
    threshold.setEndAt(endAt);
  }

  const restriction = new TaxationRestrictions();
  restriction.setMaxAmount(obj.maxAmount || obj.typologyModule?.maxAmount || 0);
  restriction.setMinAge(obj.typologyModule?.minAge || 0);
  restriction.setMaxNights(obj.maxNights || obj.typologyModule?.maxNights || 0);

  result.setId(obj.id);
  result.setTicker(obj.ticker);
  result.setActive(obj.active);
  result.setAmount(obj.typologyModule?.amount || 0);
  result.setTypology(obj.typologyModule?.typology ?? (type === TYPE.TAX ? 4 : 0));
  result.setPreference(type === TYPE.TAX ? obj.taxPreferenceModule?.preference ?? 0 : 1);
  result.setTranslationsList(translationsList);
  result.setThreshold(threshold);
  result.setRestriction(restriction);

  return result;
}

export function TypologyRenderer({ value }: RendererProps<TaxationTypology>) {
  let typology;
  switch (value) {
    case TaxationTypology.PER_PERSON:
      typology = <FormattedMessage {...messages.perPerson} />;
      break;
    case TaxationTypology.PER_PERSON_PER_NIGHT:
      typology = <FormattedMessage {...messages.perPersonPerNight} />;
      break;
    case TaxationTypology.PER_STAY:
      typology = <FormattedMessage {...messages.perStay} />;
      break;
    case TaxationTypology.PER_STAY_PER_NIGHT:
      typology = <FormattedMessage {...messages.perStayPerNight} />;
      break;
    case TaxationTypology.PERCENTAGE:
      typology = <FormattedMessage {...messages.percentage} />;
      break;
    case TaxationTypology.BALEARIC:
      typology = <FormattedMessage {...messages.balearic} />;
      break;
  }
  return <div>{typology}</div>;
}

export function ThresholdRenderer({
  value,
}: RendererProps<TaxationThreshold.AsObject | undefined>) {
  let startDate: any = <FormattedMessage {...messages.noLimit} />;
  let endDate: any = <FormattedMessage {...messages.noLimit} />;

  if (value?.startAt) {
    startDate = DateRenderer({ value: value?.startAt });
  }
  if (value?.endAt) {
    endDate = DateRenderer({ value: value?.endAt });
  }

  return (
    <div>
      {startDate} - {endDate}
    </div>
  );
}

export function transformTaxation(obj: Taxation.AsObject, type: TYPE, useTypologyModule?: boolean) {
  const clone: CustomTaxation = {
    ...cloneDeep(obj),
    typologyModule: {
      typology: obj.typology,
      amount: obj.amount,
      minAge: obj.restriction?.minAge || 0,
      typeOfTaxation: type,
      maxNights: 0,
      maxAmount: 0,
    },
    taxPreferenceModule: {
      preference: obj.preference,
    },
    maxNights: 0,
    maxAmount: 0,
    type: type,
  };

  if (useTypologyModule && clone.typologyModule) {
    clone.typologyModule.maxNights = clone.restriction?.maxNights || 0;
    clone.typologyModule.maxAmount = clone.restriction?.maxAmount || 0;
  } else {
    clone.maxNights = clone.restriction?.maxNights || 0;
    clone.maxAmount = clone.restriction?.maxAmount || 0;
  }

  clone.thresholdModule = {
    from: "",
    to: "",
  };

  if (clone.threshold?.startAt) {
    //@ts-ignore
    const from = new proto.google.protobuf.Timestamp();

    from.setNanos(obj.threshold?.startAt?.nanos);
    from.setSeconds(obj.threshold?.startAt?.seconds);

    clone.thresholdModule = {
      ...clone.thresholdModule,
      from: asUTC(from.toDate()).split("/").reverse().join("/"),
    };
  }

  if (clone.threshold?.endAt) {
    //@ts-ignore
    const to = new proto.google.protobuf.Timestamp();

    to.setNanos(obj.threshold?.endAt?.nanos);
    to.setSeconds(obj.threshold?.endAt?.seconds);

    clone.thresholdModule = {
      ...clone.thresholdModule,
      to: asUTC(to.toDate()).split("/").reverse().join("/"),
    };
  }

  return clone;
}
